import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { SurfaceCard } from '@client/components/_ui/display/SurfaceCard';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { Link } from '@client/components/Common/Link';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { createClientColumnHelper, PrismaTable, useClientTable } from '@client/hooks/table-v2';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { CarFront, LayoutDashboard } from 'lucide-react';
import { useMemo } from 'react';
import type { Metric } from '../~dashboard/sections/Claims/Fleet/fleet-data';
import { PortalMetricTile } from './PortalMetrics';
import { claimRef, daysSince } from './portal-utils';
import { type PortalPersona, PortalScopeSelect, usePortalScope } from './usePortalScope';

export const Route = createFileRoute('/console/$orgId/fleet-portal/dashboard')({
    staticData: {
        ui: {
            title: 'Dashboard',
            subtitle: 'Live claims data',
            icon: LayoutDashboard,
        },
    },
    component: FleetPortalDashboardPage,
});

type VehicleRow = RouterOutputs['fleetPortal']['listVehicles']['rows'][number];
type OffRoadRow = VehicleRow & { openClaim: NonNullable<VehicleRow['openClaim']> };

const offRoadColumnHelper = createClientColumnHelper<OffRoadRow>();

function vehicleLabel(row: VehicleRow): string {
    return [row.make, row.description].filter(Boolean).join(' ') || '—';
}

function FleetPortalDashboardPage() {
    return (
        <PageController route={Route}>
            <DashboardContent />
        </PageController>
    );
}

function DashboardContent() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const scope = usePortalScope();
    const queriesEnabled = !scope.isLoading && !scope.error;
    const statsQuery = trpc.fleetPortal.getDashboardStats.useQuery(
        { fleetClientId: scope.fleetClientId },
        { enabled: queriesEnabled },
    );
    const vehiclesQuery = trpc.fleetPortal.listVehicles.useQuery(
        { fleetClientId: scope.fleetClientId },
        { enabled: queriesEnabled },
    );

    const offRoad = vehiclesQuery.data?.rows.filter((row): row is OffRoadRow => row.openClaim !== null);

    if (scope.error) {
        return (
            <div>
                <PageLayoutHeader />
                <InlineQueryError error={scope.error} onRetry={scope.refetch} />
            </div>
        );
    }

    return (
        <div>
            <PageLayoutHeader rightAddon={<PortalScopeSelect />} />

            {statsQuery.error ? (
                <InlineQueryError error={statsQuery.error} onRetry={() => void statsQuery.refetch()} />
            ) : statsQuery.isPending || scope.isLoading ? (
                <Loading className="min-h-32" />
            ) : (
                <MetricTiles orgId={orgId} persona={scope.persona} stats={statsQuery.data} />
            )}

            <div className="mt-4">
                <div>
                    <SurfaceCard
                        icon={CarFront}
                        title="Vehicles with open claims"
                        subtitle="vehicles with an open claim, live from your claims"
                        padded={false}
                        extra={
                            <Link to="/console/$orgId/fleet-portal/fleet" params={{ orgId }}>
                                Full fleet view →
                            </Link>
                        }
                    >
                        {vehiclesQuery.error ? (
                            <div className="p-4">
                                <InlineQueryError
                                    compact
                                    error={vehiclesQuery.error}
                                    onRetry={() => void vehiclesQuery.refetch()}
                                />
                            </div>
                        ) : vehiclesQuery.isPending ? (
                            <Loading className="min-h-32" />
                        ) : (
                            <OffRoadTable orgId={orgId} persona={scope.persona} rows={offRoad ?? []} />
                        )}
                    </SurfaceCard>
                </div>
            </div>
        </div>
    );
}

/**
 * Six live metrics; only the counts and cycle time link to claim lists. The
 * selected client rides along on every link through the layout's retained
 * search param. The Pending authorisations tile follows the persona
 * (CUR-1100): a fleet manager opens the queue they decide, a coordinator opens
 * the claims list filtered to the claims awaiting a decision.
 */
function MetricTiles({
    orgId,
    persona,
    stats,
}: {
    orgId: bigint;
    persona: PortalPersona | undefined;
    stats: RouterOutputs['fleetPortal']['getDashboardStats'];
}) {
    const isCoordinator = persona === 'FMC_COORDINATOR';
    const hasPending = stats.pendingAuthorisations > 0;
    const pendingMetric: Metric = {
        label: 'Pending authorisations',
        value: String(stats.pendingAuthorisations),
        valueTone: hasPending ? 'warning' : 'default',
        note: hasPending
            ? isCoordinator
                ? 'Requests awaiting client approval; view claims →'
                : 'Awaiting approval →'
            : 'Nothing awaiting approval',
        noteTone: hasPending ? 'warning' : 'default',
    };
    const tileClassName = 'block rounded-lg no-underline ring-accent-500/40 transition hover:no-underline hover:ring-2';

    return (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Link
                to="/console/$orgId/fleet-portal/claims"
                params={{ orgId }}
                search={{ view: '__open__' }}
                className={tileClassName}
            >
                <PortalMetricTile metric={{ label: 'Open claims', value: String(stats.openClaims) }} />
            </Link>
            {isCoordinator ? (
                <Link
                    to="/console/$orgId/fleet-portal/claims"
                    params={{ orgId }}
                    search={{
                        'fleet-portal-claim-filters': [
                            {
                                id: 'tile-awaiting-authorisation',
                                filterId: 'awaitingAuthorisation',
                                operator: 'equals',
                                value: true,
                            },
                        ],
                    }}
                    className={tileClassName}
                >
                    <PortalMetricTile metric={pendingMetric} />
                </Link>
            ) : (
                <Link to="/console/$orgId/fleet-portal/authorisations" params={{ orgId }} className={tileClassName}>
                    <PortalMetricTile metric={pendingMetric} />
                </Link>
            )}
            <Link
                to="/console/$orgId/fleet-portal/claims"
                params={{ orgId }}
                search={{
                    'fleet-portal-claim-filters': [
                        {
                            id: 'tile-lodged-this-month',
                            filterId: 'lodged',
                            operator: 'withinRelative',
                            value: 'this-month',
                        },
                    ],
                }}
                className={tileClassName}
            >
                <PortalMetricTile metric={{ label: 'Lodged this month', value: String(stats.mtdLodgements) }} />
            </Link>
            <Link
                to="/console/$orgId/fleet-portal/claims"
                params={{ orgId }}
                search={{
                    'fleet-portal-claim-filters': [
                        {
                            id: 'tile-closed-this-month',
                            filterId: 'closed',
                            operator: 'withinRelative',
                            value: 'this-month',
                        },
                    ],
                }}
                className={tileClassName}
            >
                <PortalMetricTile metric={{ label: 'Closed this month', value: String(stats.mtdClosed) }} />
            </Link>

            <Link
                to="/console/$orgId/fleet-portal/claims"
                params={{ orgId }}
                search={{ view: '__closed__' }}
                className={tileClassName}
            >
                <PortalMetricTile
                    metric={{
                        label: 'Avg cycle time',
                        value: stats.avgCycleTimeDays === null ? '—' : stats.avgCycleTimeDays.toFixed(1),
                        unit: stats.avgCycleTimeDays === null ? undefined : 'days',
                        note: 'lodgement to closure',
                    }}
                />
            </Link>
            <PortalMetricTile
                metric={{
                    label: 'Avg time off road',
                    value: stats.avgTimeOffRoadDays === null ? '—' : stats.avgTimeOffRoadDays.toFixed(1),
                    unit: stats.avgTimeOffRoadDays === null ? undefined : 'days',
                    note:
                        stats.offRoadMeasuredCount === 0
                            ? 'No completed off-road periods'
                            : `Based on ${stats.offRoadMeasuredCount} completed ${stats.offRoadMeasuredCount === 1 ? 'period' : 'periods'}`,
                }}
            />
        </div>
    );
}

function OffRoadTable({
    orgId,
    persona,
    rows,
}: {
    orgId: bigint;
    persona: PortalPersona | undefined;
    rows: OffRoadRow[];
}) {
    const columns = useOffRoadColumns(orgId, persona === 'FMC_COORDINATOR');
    const tableProps = useClientTable({
        data: rows,
        columns,
        rowKey: (row) => row.id.toString(),
        pagination: false,
    });

    if (rows.length === 0) {
        return <div className="p-4 text-muted-foreground text-sm">No vehicles with open claims.</div>;
    }

    return <PrismaTable {...tableProps} />;
}

/** `showClient` adds the Client column a coordinator needs to attribute rows in the portfolio view (CUR-1100). */
function useOffRoadColumns(orgId: bigint, showClient: boolean) {
    return useMemo(
        () => [
            offRoadColumnHelper.accessor('registration', {
                id: 'rego',
                header: 'Rego',
                cell: ({ row }) => <span className="font-bold">{row.registration}</span>,
            }),
            offRoadColumnHelper.accessor((row) => vehicleLabel(row), {
                id: 'vehicle',
                header: 'Vehicle',
            }),
            ...(showClient
                ? [
                      offRoadColumnHelper.accessor('clientName', {
                          id: 'client',
                          header: 'Client',
                      }),
                  ]
                : []),
            offRoadColumnHelper.accessor((row) => row.openClaim.statusName, {
                id: 'status',
                header: 'Status',
                className: 'whitespace-nowrap',
                cell: ({ row }) => (
                    <ClaimStatusTag
                        data={{ name: row.openClaim.statusName, displayColor: row.openClaim.statusDisplayColor }}
                    />
                ),
            }),
            offRoadColumnHelper.accessor((row) => row.openClaim.repairStage, {
                id: 'repairStage',
                header: 'Repair stage',
                cell: ({ row }) => row.openClaim.repairStage ?? 'Awaiting assessment',
            }),
            offRoadColumnHelper.accessor((row) => daysSince(row.openClaim.createdAt), {
                id: 'days',
                header: 'Days since lodged',
                align: 'right',
                cell: ({ row }) => daysSince(row.openClaim.createdAt) ?? '—',
            }),
            offRoadColumnHelper.accessor('id', {
                id: 'claim',
                header: 'Claim',
                align: 'right',
                cell: ({ row }) => (
                    <Link
                        to="/console/$orgId/fleet-portal/claims/details/$claimId"
                        params={{ orgId, claimId: row.openClaim.id }}
                    >
                        {claimRef(row.openClaim)}
                    </Link>
                ),
            }),
        ],
        [orgId, showClient],
    );
}
